.page {
  min-height: 100%;
  display: grid;
  place-items: center;
  padding: var(--space-4);
  position: relative;
  overflow: hidden;
  background: var(--surface-void);
}

/* A wide, low-alpha band, slow enough to read as light rather than animation. */
.page::before {
  content: '';
  position: absolute;
  inset: -40%;
  pointer-events: none;
  opacity: var(--gloss-enabled);
  background: radial-gradient(
    38% 60% at 50% 0%,
    rgb(255 255 255 / 5%) 0%,
    rgb(255 255 255 / 0%) 70%
  );
  animation: drift 34s var(--ease-out) infinite alternate;
}

@keyframes drift {
  from {
    transform: translate3d(-14%, -6%, 0) rotate(-8deg);
  }
  to {
    transform: translate3d(14%, 6%, 0) rotate(8deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .page::before {
    animation: none;
  }
}

.center {
  position: relative;
  z-index: 1;
  width: min(26rem, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.themeSlot {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  z-index: 2;
}

/* The negative right margin absorbs the trailing letter-space, so this stays
 * optically centred. */
.wordmark {
  margin: 0;
  font-family: var(--font-chrome);
  font-size: clamp(0.9375rem, 3.2vw, 1.125rem);
  font-weight: 400;
  letter-spacing: var(--track-wide);
  text-transform: uppercase;
  color: var(--ink-bright);
  margin-right: calc(var(--track-wide) * -1);
  text-align: center;
}

.tagline {
  margin: var(--space-2) 0 0;
  font-size: var(--step-1);
  color: var(--ink-dim);
  text-align: center;
}

.panel {
  margin-top: var(--space-5);
  width: 100%;
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  background: linear-gradient(180deg, var(--surface-raised), var(--surface-panel));
  border: 1px solid var(--edge);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lift);
}

.label {
  font-family: var(--font-chrome);
  font-size: var(--step-0);
  letter-spacing: var(--track-mid);
  text-transform: uppercase;
  color: var(--ink-dim);
}

/*
 * Transcribed data, not prose: every character has to be unambiguous read off a
 * phone. Same reasoning that removed I, L, O, U, 0 and 1 from the alphabet.
 */
.input {
  width: 100%;
  padding: var(--space-2);
  font-family: var(--font-chrome);
  font-size: var(--step-4);
  letter-spacing: 0.22em;
  text-align: center;
  text-transform: uppercase;
  color: var(--ink-bright);
  background: var(--surface-void);
  border: 1px solid var(--edge-strong);
  border-radius: var(--radius);
  transition: border-color 160ms var(--ease-out);
}

.input:hover:not(:disabled) {
  border-color: var(--ink-faint);
}

.input:disabled {
  opacity: 0.6;
}

.input[aria-invalid='true'] {
  border-color: var(--signal);
}

.submit {
  padding: var(--space-2);
  font-family: var(--font-chrome);
  font-size: var(--step-0);
  letter-spacing: var(--track-mid);
  text-transform: uppercase;
  color: var(--surface-void);
  background: var(--ink-bright);
  border: 1px solid transparent;
  border-radius: var(--radius);
  cursor: pointer;
  transition:
    opacity 160ms var(--ease-out),
    transform 160ms var(--ease-out);
}

.submit:hover:not(:disabled) {
  opacity: 0.88;
}

.submit:active:not(:disabled) {
  transform: translateY(1px);
}

.submit:disabled {
  opacity: 0.32;
  cursor: default;
}

/* Height is reserved either way, so the panel never jumps when an error lands. */
.error {
  min-height: 1.5rem;
  margin: var(--space-2) 0 0;
  font-size: var(--step-1);
  color: var(--signal-ink);
  text-align: center;
}

.contact {
  margin-top: var(--space-5);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-2);
  font-size: var(--step-1);
  color: var(--ink-faint);
}

.contactLink {
  color: var(--ink-dim);
  text-decoration: none;
  border-bottom: 1px solid var(--edge-strong);
  transition: color 160ms var(--ease-out);
}

.contactLink:hover {
  color: var(--ink-bright);
}
